'use client';

import { use } from 'react';
import { Configure, Index } from 'react-instantsearch';
import { Spacing } from '@ghosts-inc/design-system/constants';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AnalyticsEventNames } from '@/analytics/Event.types';
import { AnalyticsPageNames } from '@/analytics/Page.types';
import { PageContext, SectionContext } from '@/analytics/analytics.types';
import { analytics } from '@/analytics/segment';
import Link from '@/components/Link';
import { BuyerListingFavorite } from '@/legacy/favorites/FavoritesViewModel';
import { useGhostStore } from '@/legacy/store';
import { Collection } from '@/types/collections';
import Section from '../Section';
import { Background } from '../types';
import { CollectionVisibilityProvider } from './CollectionContext';
import SearchState from './CollectionSearchState';
import { SectionColorContext } from './SectionColorContext';
import { ConfigProps } from './types';

const GenericCollectionContent = ({
  listingsIndexName,
  favoritesPromise,
  configureProps,
  collection,
  ...rest
}: {
  listingsIndexName: string;
  favoritesPromise: Promise<BuyerListingFavorite[]>;
  sectionTitle: string;
  mobileDisplayText?: string;
  seeMoreStringComponent: JSX.Element;
  navigateTo: string;
  numPerPage: number;
  favorites?: BuyerListingFavorite[];
  analyticsInfo: {
    context: PageContext & SectionContext;
  };
  collection?: Collection;
  configureProps: ConfigProps;
  background?: Background;
  isStockXSectionEnabled: boolean;
}): JSX.Element => {
  const favorites = use(favoritesPromise);
  const buyerId = useGhostStore((state) => state.user)?.buyer?.id;

  const stealthListingFilter = buyerId
    ? `(stealthApprovedBuyerIds:${buyerId} OR isStealth:false)`
    : 'isStealth:false';

  return (
    <Index indexName={listingsIndexName} indexId={rest.sectionTitle}>
      <Configure hitsPerPage={10} filters={stealthListingFilter} {...configureProps} />
      <SearchState collection={collection} favorites={favorites} {...rest} />
    </Index>
  );
};

export default function GenericCollection({
  collection,
  sectionTitle,
  listingsIndexName,
  background: defaultBackground,
  navigateTo,
  numPerPage,
  favoritesPromise,
  seeMoreStringComponent,
  configureProps,
  index,
  isStockXSectionEnabled,
}: {
  collection?: Collection;
  listingsIndexName: string;
  sectionTitle: string;
  background?: Background;
  navigateTo: string;
  numPerPage: number;
  favoritesPromise: Promise<BuyerListingFavorite[]>;
  seeMoreStringComponent?: JSX.Element;
  configureProps: ConfigProps;
  index: number;
  isStockXSectionEnabled: boolean;
}): JSX.Element | null {
  // Shared among multiple analytics calls in this section, so we define it once here
  const analyticsInfo: {
    context: PageContext & SectionContext;
  } = {
    context: { page: AnalyticsPageNames.BUYER_HOME, section: sectionTitle },
  };

  const { backgrounds } = use(SectionColorContext);

  const background = backgrounds?.get(index) || defaultBackground;

  const textColor = background === 'dark' ? 'typographyDarkInverted' : 'typographyDarkPrimary';
  const getSeeMoreComponent = () => {
    if (seeMoreStringComponent) return seeMoreStringComponent;
    if (collection === 'new' || collection === 'featured' || collection === 'trending') {
      return (
        <>
          Looking for more
          <br />
          {collection} inventory?
        </>
      );
    }
    return <>Looking for more?</>;
  };

  return (
    <CollectionVisibilityProvider index={index}>
      <Section background={background}>
        <Stack flexDirection="column">
          <Stack flexDirection="row" justifyContent="space-between" alignItems="flex-end">
            <Typography variant="h2" color={textColor}>
              {sectionTitle}
            </Typography>

            <Link
              variant="link"
              data-testid={`view-all-link-${collection}`}
              href={navigateTo}
              color={textColor}
              whiteSpace="nowrap"
              ml={Spacing.large}
              display={{ mobile: 'none', tablet: 'block' }}
              onClick={() => {
                analytics.track(AnalyticsEventNames.HOME_SECTION_VIEW_ALL_CLICKED, {
                  ...analyticsInfo,
                });
              }}
            >
              View All
            </Link>
          </Stack>
        </Stack>
        <Box
          position="relative"
          minHeight={{ mobile: 313, smallTablet: 330 }}
          mt={{ mobile: 3, desktop: 3 }}
        >
          <GenericCollectionContent
            favoritesPromise={favoritesPromise}
            listingsIndexName={listingsIndexName}
            sectionTitle={sectionTitle}
            analyticsInfo={analyticsInfo}
            navigateTo={navigateTo}
            numPerPage={numPerPage}
            seeMoreStringComponent={getSeeMoreComponent()}
            configureProps={configureProps}
            background={background}
            collection={collection}
            isStockXSectionEnabled={isStockXSectionEnabled}
          />
        </Box>
      </Section>
    </CollectionVisibilityProvider>
  );
}
